import { AccountType } from '../page/my-account/account-state';
import { routerManager } from '../router.manager';
import { RoutesType } from '../router.config';

/**
 * 路由权限配置
 * 将路由路径映射到允许的账户类型
 */
export const ROUTE_PERMISSIONS: Record<string, AccountType[]> = {
    // 管理员相关路由
    '/main/business': ['ADMIN', 'RESELLER'],
    '/main/group': ['ADMIN', 'RESELLER'],
    '/main/recycle_bin': ['ADMIN', 'RESELLER'],
    '/main/tunnel-management': ['ADMIN', 'RESELLER'],
    '/main/qop-tunnel-management': ['ADMIN', 'RESELLER'],
    '/main/setting': ['ADMIN'],
    '/main/shark': ['ADMIN', 'RESELLER'],
    '/main/login-operation': ['ADMIN', 'RESELLER'],
    '/main/reseller-list': ['ADMIN'],
    '/main/reseller-consumption-list': ['ADMIN', 'RESELLER'],
    '/main/product-list': ['ADMIN'],
    '/main/anagrafica-ocr-sessions': ['ADMIN', 'RESELLER'],

    // 员工相关路由
    '/main/analysis': ['STAFF'],
    '/main/advanced-business-analysis': ['STAFF'],
    '/main/intelligent-risk-control': ['STAFF'],
    '/main/group-revenue-analysis': ['STAFF'],
    '/main/menu-library-management': ['STAFF'],
    '/main/menu-store': ['STAFF'],
    '/main/fiskaly': ['STAFF'],
    '/main/rbp-cloud': ['STAFF'],

    // 公共路由（所有角色都可访问）
    '/main/account': ['ADMIN', 'STAFF', 'RESELLER'],
    '/main/help-center': ['ADMIN', 'STAFF', 'RESELLER'],
};

/**
 * 默认首页映射
 */
export const DEFAULT_HOME_ROUTES: Record<AccountType, string> = {
    ADMIN: '/main/group',
    RESELLER: '/main/group',
    STAFF: '/main/current-business-status',
};

/**
 * 登录页面路径映射
 */
export const LOGIN_ROUTES: Record<AccountType, string> = {
    ADMIN: '/admin',
    RESELLER: '/reseller',
    STAFF: '/staff',
};

/**
 * 权限管理器类
 */
export class PermissionManager {
    /**
     * 检查用户是否有权限访问指定路由
     * @param path 路由路径
     * @param accountType 账户类型
     * @returns 是否有权限
     */
    static hasPermission(path: string, accountType: AccountType | undefined): boolean {
        if (!accountType) {
            return false;
        }

        // 检查精确匹配
        if (ROUTE_PERMISSIONS[path]) {
            return ROUTE_PERMISSIONS[path].includes(accountType);
        }

        // 检查前缀匹配（用于嵌套路由）
        for (const [routePath, allowedTypes] of Object.entries(ROUTE_PERMISSIONS)) {
            if (path.startsWith(routePath)) {
                return allowedTypes.includes(accountType);
            }
        }

        // 默认允许（对于未配置的路由）
        return true;
    }

    /**
     * 获取用户的默认首页
     * @param accountType 账户类型
     * @returns 默认首页路径
     */
    static getDefaultHomePath(accountType: AccountType | undefined): string {
        if (!accountType) {
            return LOGIN_ROUTES.STAFF;
        }
        return DEFAULT_HOME_ROUTES[accountType];
    }

    /**
     * 获取登录页面路径
     * @param path 当前路径
     * @returns 登录页面路径
     */
    static getLoginPath(path: string): string {
        if (path.includes('/admin')) {
            return LOGIN_ROUTES.ADMIN;
        }
        if (path.includes('/reseller')) {
            return LOGIN_ROUTES.RESELLER;
        }
        return LOGIN_ROUTES.STAFF;
    }

    /**
     * 根据路由配置过滤用户可访问的路由
     * @param routes 路由配置
     * @param accountType 账户类型
     * @returns 过滤后的路由配置
     */
    static filterRoutesByPermission(
        routes: RoutesType[],
        accountType: AccountType | undefined
    ): RoutesType[] {
        if (!accountType) {
            return [];
        }

        return routes
            .map(route => {
                // 检查当前路由权限
                const hasAccess = !route.only || route.only.includes(accountType);

                if (!hasAccess) {
                    return null;
                }

                // 递归过滤子路由
                if (route.children && route.children.length > 0) {
                    const filteredChildren = this.filterRoutesByPermission(
                        route.children,
                        accountType
                    );

                    return {
                        ...route,
                        children: filteredChildren,
                    };
                }

                return route;
            })
            .filter((route): route is RoutesType => route !== null);
    }

    /**
     * 检查路由路径是否需要重定向
     * @param currentPath 当前路径
     * @param accountType 账户类型
     * @returns 重定向路径（如果需要）或 null
     */
    static getRedirectPath(
        currentPath: string,
        accountType: AccountType | undefined
    ): string | null {
        if (!accountType) {
            return this.getLoginPath(currentPath);
        }

        // 如果当前路径没有权限，重定向到默认首页
        if (!this.hasPermission(currentPath, accountType)) {
            return this.getDefaultHomePath(accountType);
        }

        return null;
    }

    /**
     * 读取当前登录页 pathname（HashRouter 下为 `#` 后路径）。
     * 优先 React Router history；未绑定或不在登录入口时从 `location.hash` 解析，避免 SPA 切换入口后仍误读旧路径。
     */
    static getLoginPortalPathname(): string {
        /** React Router 注入的 pathname */
        const fromRouter = routerManager.history?.location?.pathname as string | undefined;
        if (fromRouter && this.getAccountTypeFromLoginPath(fromRouter)) {
            return fromRouter;
        }
        if (typeof window === 'undefined') {
            return '';
        }
        /** 兜底：从 hash 解析 `#/admin` 等登录入口 */
        const raw = window.location.hash || '';
        const withoutLeading = raw.startsWith('#') ? raw.slice(1) : raw;
        return (withoutLeading.split('?')[0] || '').trim();
    }

    /**
     * 从登录页路由（/admin、/staff、/reseller）解析当前登录入口对应的账号类型。
     * @param pathname - 当前 pathname（HashRouter 下不含 #）
     */
    static getAccountTypeFromLoginPath(pathname: string): AccountType | undefined {
        if (pathname.includes(LOGIN_ROUTES.ADMIN)) {
            return 'ADMIN';
        }
        if (pathname.includes(LOGIN_ROUTES.RESELLER)) {
            return 'RESELLER';
        }
        if (pathname.includes(LOGIN_ROUTES.STAFF)) {
            return 'STAFF';
        }
        return undefined;
    }

    /**
     * 登录入口与 token 实际账号类型不一致时，提示用户应使用的登录入口 i18n key。
     * @param accountType - token metadata 中的真实账号类型
     */
    static getLoginPortalMismatchI18nKey(accountType: AccountType): string {
        if (accountType === 'ADMIN') {
            return 'general_text_please_login_using_the_admin_account';
        }
        if (accountType === 'RESELLER') {
            return 'general_text_please_login_using_an_reseller_account';
        }
        return 'general_text_please_login_using_an_staff_account';
    }

    /**
     * 验证账户类型是否匹配当前路由
     * @param currentPath 当前路径
     * @param accountType 账户类型
     * @returns 是否匹配
     */
    static validateAccountTypeForRoute(
        currentPath: string,
        accountType: AccountType | undefined
    ): boolean {
        if (!accountType) {
            return false;
        }

        // ADMIN 路由验证
        if (currentPath.includes('/admin') && accountType !== 'ADMIN') {
            return false;
        }

        // RESELLER 路由验证
        if (currentPath.includes('/reseller') && accountType !== 'RESELLER') {
            return false;
        }

        // STAFF 路由验证
        if (currentPath.includes('/staff') && accountType !== 'STAFF') {
            return false;
        }

        return true;
    }
}
